import React, { useState, useEffect } from 'react';
import api from "../lib/api";
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useWebSocket } from '../hooks/useWebSocket';

const POPULAR_PAIRS = ['BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'SOL/USDT', 'XRP/USDT'];

export default function MarketView() {
  const [selectedPair, setSelectedPair] = useState('BTC/USDT');
//  const [selectedExchange, setSelectedExchange] = useState('coinbase');
  const [ticker, setTicker] = useState(null);
   const [selectedExchange, setSelectedExchange] = useState('coinbase');
  const [chartData, setChartData] = useState([]);
  const [scannerResults, setScannerResults] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { isConnected, lastMessage, subscribe, unsubscribe } = useWebSocket();

  useEffect(() => {
    if (isConnected) {
      subscribe(selectedExchange, selectedPair);
      return () => unsubscribe(selectedExchange, selectedPair);
    }
  }, [isConnected, selectedPair, selectedExchange]);

  useEffect(() => {
    if (lastMessage?.type === 'price_update' && lastMessage.exchange === selectedExchange && lastMessage.symbol === selectedPair) {
      setTicker({ symbol: selectedPair, exchange: selectedExchange, ...lastMessage.data });
    }
  }, [lastMessage]);

  useEffect(() => {
    fetchMarketData();
    fetchScanner();
  }, [selectedPair, selectedExchange]);

  const fetchMarketData = async () => {
    try {
      const [tickerRes, ohlcvRes] = await Promise.all([
        api.get(`/market/ticker/${selectedPair}`, { params: { exchange: selectedExchange } }),
        api.get(`/market/ohlcv/${selectedPair}`, { params: { exchange: selectedExchange, timeframe: '1h', limit: 24 } })
      ]);
      setTicker(tickerRes.data);
      const chart = ohlcvRes.data.candles.map(c => ({
        time: new Date(c.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        price: c.close
      }));
      setChartData(chart);
    } catch (error) {
      toast.error('Failed to fetch market data');
    } finally {
      setLoading(false);
    }
  };

  const fetchScanner = async () => {
    try {
      const res = await api.get('/market/scanner', { params: { exchange: 'coinbase' } });
      setScannerResults(res.data.results);
    } catch (error) {
      console.error('Scanner failed');
    }
  };

  if (loading) return <div className="text-text-muted">Loading...</div>;

  const priceChange = ticker?.change_24h || 0;

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold font-heading text-text-main">MARKET DATA</h1>

      <div className="flex gap-4">
        <div>
          <label className="block text-xs text-text-muted mb-2 uppercase font-bold">Pair</label>
          <select value={selectedPair} onChange={(e) => setSelectedPair(e.target.value)} className="px-4 py-2 bg-surface border border-border text-text-main rounded-sm">
            {POPULAR_PAIRS.map(pair => <option key={pair} value={pair}>{pair}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-text-muted mb-2 uppercase font-bold">Exchange</label>
          <select value={selectedExchange} onChange={(e) => setSelectedExchange(e.target.value)} className="px-4 py-2 bg-surface border border-border text-text-main rounded-sm">
            <option value="binanceus">Binance.us</option>
            <option value="coinbase">Coinbase</option>
            <option value="kraken">Kraken</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface border border-border/40 rounded-sm p-4">
          <div className="text-xs uppercase text-text-muted font-bold mb-2">Last Price</div>
          <div className="text-3xl font-mono font-bold text-text-main">${ticker?.last?.toFixed(2)}</div>
        </div>
        <div className="bg-surface border border-border/40 rounded-sm p-4">
          <div className="text-xs uppercase text-text-muted font-bold mb-2">24h Change</div>
          <div className={`text-3xl font-mono font-bold ${priceChange >= 0 ? 'text-secondary' : 'text-destructive'}`}>
            {priceChange >= 0 ? '+' : ''}{priceChange?.toFixed(2)}%
          </div>
        </div>
        <div className="bg-surface border border-border/40 rounded-sm p-4">
          <div className="text-xs uppercase text-text-muted font-bold mb-2">24h High</div>
          <div className="text-3xl font-mono font-bold text-text-main">${ticker?.high_24h?.toFixed(2)}</div>
        </div>
        <div className="bg-surface border border-border/40 rounded-sm p-4">
          <div className="text-xs uppercase text-text-muted font-bold mb-2">24h Low</div>
          <div className="text-3xl font-mono font-bold text-text-main">${ticker?.low_24h?.toFixed(2)}</div>
        </div>
      </div>

      <div className="bg-surface border border-border/40 rounded-sm p-6">
        <h2 className="text-xl font-bold uppercase font-heading mb-4">Price Chart (24H)</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222222" />
            <XAxis dataKey="time" stroke="#888888" style={{ fontSize: '12px' }} />
            <YAxis stroke="#888888" style={{ fontSize: '12px' }} domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ backgroundColor: '#0A0A0A', border: '1px solid #222222' }} />
            <Line type="monotone" dataKey="price" stroke="#00F0FF" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-surface border border-border/40 rounded-sm p-6">
        <h2 className="text-xl font-bold uppercase font-heading mb-4">Market Scanner</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-text-muted">
                <th className="text-left py-3 px-3 uppercase text-xs font-bold">Symbol</th>
                <th className="text-right py-3 px-3 uppercase text-xs font-bold">Price</th>
                <th className="text-right py-3 px-3 uppercase text-xs font-bold">24h Change</th>
                <th className="text-right py-3 px-3 uppercase text-xs font-bold">RSI</th>
                <th className="text-center py-3 px-3 uppercase text-xs font-bold">Signal</th>
              </tr>
            </thead>
            <tbody>
              {scannerResults.map(result => (
                <tr key={result.symbol} className="border-b border-border/50 hover:bg-white/5">
                  <td className="py-3 px-3 font-mono font-bold text-text-main">{result.symbol}</td>
                  <td className="py-3 px-3 font-mono text-right text-text-main">${result.price?.toFixed(2)}</td>
                  <td className={`py-3 px-3 font-mono text-right ${result.change_24h >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                    {result.change_24h >= 0 ? '+' : ''}{result.change_24h?.toFixed(2)}%
                  </td>
                  <td className="py-3 px-3 font-mono text-right text-text-main">{result.rsi?.toFixed(1)}</td>
                  <td className="py-3 px-3 text-center">
                    <span className={`text-xs px-3 py-1 rounded uppercase font-bold ${result.signal === 'BUY' ? 'bg-secondary/10 text-secondary border border-secondary/20' : result.signal === 'SELL' ? 'bg-destructive/10 text-destructive border border-destructive/20' : 'bg-border text-text-muted'}`}>
                      {result.signal}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
